
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileCheck, Download, Eye, Loader2, Edit, X, Save, Calendar, DollarSign, User, FileText, Search, Upload } from "lucide-react";
import { useContratosPaginated, useContratosCount, useAtualizarContrato, type ContratosFiltroParams } from "@/hooks/useContratos";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileInput } from "@/components/ui/file-input";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { ListagemAcoes, type ListagemColumn } from "@/components/listagem/ListagemAcoes";
import { useCidadesPorEstado } from "@/hooks/useClientesEstadosCidades";
import { openStorageUrl, uploadFileToStorage } from "@/lib/storageUrl";
import { BRASIL_UFS } from "@/lib/estados";
import { useMemo } from "react";
import { formatNumeroCicloVenda } from "@/lib/pedidosCiclo";

export function ContratosTable() {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [buscaContratoInput, setBuscaContratoInput] = useState(""); // Estado local para o input
  const [buscaContrato, setBuscaContrato] = useState(""); // Estado para a query (com debounce)

  const filtrosDraftDefault = useMemo(
    () => ({
      dataInicio: "",
      dataFim: "",
      status: "",
      valorMin: "",
      valorMax: "",
      estado: "",
      cidade: "",
    }),
    []
  );
  const [filtrosDraft, setFiltrosDraft] = useState(filtrosDraftDefault);
  const [filtrosAplicados, setFiltrosAplicados] = useState(filtrosDraftDefault);
  const { data: cidadesDoEstado = [] } = useCidadesPorEstado(filtrosDraft.estado);

  const toNum = (v: string) => {
    const s = (v ?? "").trim();
    if (!s) return undefined;
    const n = Number(s.replace(/\./g, "").replace(",", "."));
    return Number.isFinite(n) ? n : undefined;
  };

  const filtrosHook = useMemo<ContratosFiltroParams>(() => {
    const periodo =
      filtrosAplicados.dataInicio || filtrosAplicados.dataFim
        ? { dataInicio: filtrosAplicados.dataInicio || undefined, dataFim: filtrosAplicados.dataFim || undefined }
        : null;
    const valorMin = toNum(filtrosAplicados.valorMin);
    const valorMax = toNum(filtrosAplicados.valorMax);
    return {
      periodo,
      status: filtrosAplicados.status || null,
      valorMin: typeof valorMin === "number" ? valorMin : null,
      valorMax: typeof valorMax === "number" ? valorMax : null,
      estado: filtrosAplicados.estado || null,
      cidade: filtrosAplicados.cidade || null,
    };
  }, [filtrosAplicados]);
  
  // Debounce: atualizar busca após 700ms sem digitação (reduz significativamente requisições ao banco)
  useEffect(() => {
    // Se o campo estiver vazio, limpar busca imediatamente
    if (buscaContratoInput === "") {
      setBuscaContrato("");
      return;
    }

    // Para campos com texto, aguardar delay para evitar queries excessivas
    const timer = setTimeout(() => {
      setBuscaContrato(buscaContratoInput);
    }, 700);

    return () => clearTimeout(timer);
  }, [buscaContratoInput]);
  
  // Resetar para página 1 quando busca mudar
  useEffect(() => {
    setCurrentPage(1);
  }, [buscaContrato, filtrosHook]);
  
  // Buscar contratos paginados e total
  const { data: contratosResult, isLoading, error } = useContratosPaginated(currentPage, itemsPerPage, buscaContrato, filtrosHook);
  const contratos = contratosResult?.data || [];
  const totalContratos = contratosResult?.count || 0;
  const atualizarContrato = useAtualizarContrato();
  const queryClient = useQueryClient();
  
  // Estados para os modais
  const [showVisualizarModal, setShowVisualizarModal] = useState(false);
  const [showEditarModal, setShowEditarModal] = useState(false);
  const [showAnexarModal, setShowAnexarModal] = useState(false);
  const [contratoSelecionado, setContratoSelecionado] = useState<any>(null);
  const [editandoContrato, setEditandoContrato] = useState<any>(null);
  const [arquivoParaAnexar, setArquivoParaAnexar] = useState<File | null>(null);
  const [uploadingArquivo, setUploadingArquivo] = useState(false);

  const contratoColumns: ListagemColumn<any>[] = [
    { key: "id", label: "ID", value: (c) => c.id },
    { key: "cliente", label: "Cliente", value: (c) => c.cliente?.nome ?? (c.cliente_id ? `Cliente ${c.cliente_id}` : "—") },
    { key: "localizacao", label: "Estado/Cidade", value: (c) => `${c.cliente?.estado ?? "—"} / ${c.cliente?.cidade ?? "—"}` },
    { key: "valor", label: "Valor", value: (c) => formatarValor(c.valor) },
    { key: "status", label: "Status", value: (c) => c.status ?? "" },
    { key: "renovacao", label: "Renovação", value: (c) => formatNumeroCicloVenda(c.numero_ciclo) },
    { key: "data_inicio", label: "Data Início", value: (c) => (c.data_inicio ? new Date(c.data_inicio).toLocaleDateString("pt-BR") : "-") },
    { key: "data_fim", label: "Data Fim", value: (c) => (c.data_fim ? new Date(c.data_fim).toLocaleDateString("pt-BR") : "-") },
    { key: "arquivo", label: "Arquivo", value: (c) => (c.arquivo_pdf_url ? "Sim" : "Não") },
  ];
  const [visibleContratoCols, setVisibleContratoCols] = useState<string[]>(contratoColumns.map((c) => c.key));
  const toggleContratoCol = (key: string, nextVisible: boolean) => {
    setVisibleContratoCols((prev) => (nextVisible ? Array.from(new Set([...prev, key])) : prev.filter((k) => k !== key)));
  };

  const handleAplicarFiltros = () => {
    setFiltrosAplicados(filtrosDraft);
    setCurrentPage(1);
  };

  const handleLimparFiltros = () => {
    setFiltrosDraft(filtrosDraftDefault);
    setFiltrosAplicados(filtrosDraftDefault);
    setBuscaContratoInput("");
    setBuscaContrato("");
    setCurrentPage(1);
  };

  const visualizarContrato = (contrato: any) => {
    setContratoSelecionado(contrato);
    setShowVisualizarModal(true);
  };

  const baixarPDF = (contrato: any) => {
    if (contrato.arquivo_pdf_url) {
      void openStorageUrl(contrato.arquivo_pdf_url);
      toast.success('PDF aberto em nova aba!');
    } else {
      toast.error('Nenhum arquivo PDF anexado a este contrato');
    }
  };

  const editarContrato = (contrato: any) => {
    setEditandoContrato({
      ...contrato,
      data_inicio: contrato.data_inicio ? new Date(contrato.data_inicio).toISOString().split('T')[0] : '',
      data_fim: contrato.data_fim ? new Date(contrato.data_fim).toISOString().split('T')[0] : ''
    });
    setShowEditarModal(true);
  };

  const abrirModalAnexar = (contrato: any) => {
    setContratoSelecionado(contrato);
    setArquivoParaAnexar(null);
    setShowAnexarModal(true);
  };

  const uploadArquivoContrato = async (file: File, contratoId: number) => {
    try {
      console.log(`Iniciando upload do arquivo do contrato ${contratoId}`);
      
      // Verificar se o arquivo existe
      if (!file) {
        throw new Error('Arquivo não fornecido');
      }

      // Verificar tamanho do arquivo (máximo 20MB)
      const maxSize = 20 * 1024 * 1024; // 20MB
      if (file.size > maxSize) {
        throw new Error('Arquivo muito grande. Máximo 20MB permitido.');
      }

      // Verificar se é PDF
      if (!file.type.includes('pdf')) {
        throw new Error('Apenas arquivos PDF são permitidos.');
      }

      const timestamp = Date.now();
      const fileName = `contrato_${contratoId}_${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const filePath = `contratos/${contratoId}/${fileName}`;
      const { publicUrl } = await uploadFileToStorage(filePath, file);

      // Atualizar o contrato com a URL do arquivo
      const { error: updateError } = await supabase
        .from('contratos')
        .update({
          arquivo_pdf_url: publicUrl,
          arquivo_pdf_name: file.name
        })
        .eq('id', contratoId);

      if (updateError) {
        console.error('Erro ao atualizar contrato com URL do arquivo:', updateError);
        throw updateError;
      }

      console.log('Arquivo do contrato enviado com sucesso:', publicUrl);
      return { publicUrl, fileName: file.name };
      
    } catch (error) {
      console.error('Erro ao fazer upload do arquivo do contrato:', error);
      throw error;
    }
  };

  const handleAnexarArquivo = async () => {
    if (!contratoSelecionado || !arquivoParaAnexar) {
      toast.error("Por favor, selecione um arquivo PDF");
      return;
    }

    setUploadingArquivo(true);
    try {
      await uploadArquivoContrato(arquivoParaAnexar, contratoSelecionado.id);
      toast.success("Arquivo anexado com sucesso!");
      
      // Invalidar cache para recarregar a listagem
      queryClient.invalidateQueries({ queryKey: ['contratos'] });
      
      setShowAnexarModal(false);
      setArquivoParaAnexar(null);
      setContratoSelecionado(null);
    } catch (error: any) {
      console.error('Erro ao anexar arquivo:', error);
      toast.error(`Erro ao anexar arquivo: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setUploadingArquivo(false);
    }
  };

  const salvarEdicao = async () => {
    if (!editandoContrato) return;

    try {
      await atualizarContrato.mutateAsync({
        id: editandoContrato.id,
        status: editandoContrato.status,
        data_fim: editandoContrato.data_fim,
        observacoes: editandoContrato.observacoes
      });

      toast.success('Contrato atualizado com sucesso!');
      setShowEditarModal(false);
      setEditandoContrato(null);
    } catch (error) {
      console.error('Erro ao atualizar contrato:', error);
      toast.error('Erro ao atualizar contrato');
    }
  };

  const formatarData = (data: string) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const formatarValor = (valor: number) => {
    return `R$ ${Number(valor).toFixed(2).replace('.', ',')}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ativo':
        return 'bg-green-100 text-green-800';
      case 'aguardando pagamento':
        return 'bg-amber-100 text-amber-800';
      case 'pendente':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelado':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <Card className="border-0 shadow-md">
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Carregando contratos...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-0 shadow-md">
        <CardContent className="p-8">
          <div className="text-center text-red-600">
            Erro ao carregar contratos: {error.message}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-0 shadow-md">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2 text-gray-900">
              <FileCheck className="h-5 w-5 text-green-600" />
              Contratos Ativos ({totalContratos})
            </CardTitle>
            <div className="flex items-center gap-2">
              <ListagemAcoes
                title="Contratos"
                subtitle={`Página ${currentPage} • Busca: ${buscaContratoInput || "—"}`}
                rows={contratos}
                columns={contratoColumns}
                visibleKeys={visibleContratoCols}
                onToggleColumn={toggleContratoCol}
                filenameBase={`contratos_p${currentPage}`}
                enableExport
                fetchAllRows={async () => {
                  const pageSize = 1000;
                  let offset = 0;
                  const all: any[] = [];

                  const clienteFilterPresent = !!(
                    (filtrosHook?.estado && filtrosHook.estado.trim()) ||
                    (filtrosHook?.cidade && filtrosHook.cidade.trim())
                  );

                  // eslint-disable-next-line no-constant-condition
                  while (true) {
                    let query = supabase
                      .from("contratos")
                      .select(
                        `
                        *,
                        cliente:clientes${clienteFilterPresent ? "!inner" : ""}(id, nome, email, telefone, cidade, estado),
                        pedido:pedidos!pedido_id(id, numero_pedido, status),
                        projeto:projetos!projeto_id(id, status)
                      `
                      )
                      .order("id", { ascending: false });

                    if (filtrosHook?.periodo?.dataInicio) query = query.gte("data_inicio", filtrosHook.periodo.dataInicio);
                    if (filtrosHook?.periodo?.dataFim) query = query.lte("data_inicio", filtrosHook.periodo.dataFim);
                if (filtrosHook?.status) query = query.eq("status", filtrosHook.status);
                if (typeof filtrosHook?.valorMin === "number") query = query.gte("valor", filtrosHook.valorMin);
                    if (typeof filtrosHook?.valorMax === "number") query = query.lte("valor", filtrosHook.valorMax);
                    if (filtrosHook?.estado) query = query.eq("cliente.estado", filtrosHook.estado.toUpperCase());
                    if (filtrosHook?.cidade) query = query.eq("cliente.cidade", filtrosHook.cidade);

                    if (buscaContrato && buscaContrato.trim()) {
                      const searchLower = buscaContrato.trim().toLowerCase();
                      if (!isNaN(Number(buscaContrato.trim()))) {
                        query = query.eq("id", parseInt(buscaContrato.trim()));
                      } else {
                        const { data: clientesEncontrados } = await supabase
                          .from("clientes")
                          .select("id")
                          .ilike("nome", `%${searchLower}%`);
                        if (clientesEncontrados && clientesEncontrados.length > 0) {
                          const clienteIds = clientesEncontrados.map((c: any) => c.id);
                          query = query.or(
                            `numero_contrato.ilike.%${searchLower}%,status.ilike.%${searchLower}%,cliente_id.in.(${clienteIds.join(",")})`
                          );
                        } else {
                          query = query.or(`numero_contrato.ilike.%${searchLower}%,status.ilike.%${searchLower}%`);
                        }
                      }
                    }

                    const { data, error } = await query.range(offset, offset + pageSize - 1);
                    if (error) throw error;
                    const rows = data || [];
                    if (rows.length === 0) break;
                    all.push(...rows);
                    if (rows.length < pageSize) break;
                    offset += pageSize;
                  }

                  return all;
                }}
              />
            </div>
          </div>
          <div className="mt-4 rounded-md bg-[#C9C9C9] p-3 text-xs">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-8">
              <div className="lg:col-span-2">
                <Label className="text-xs">Buscar</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input
                    placeholder="ID, número, cliente ou status..."
                    value={buscaContratoInput}
                    onChange={(e) => setBuscaContratoInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        setBuscaContrato(buscaContratoInput);
                      }
                    }}
                    className="h-8 pl-9 text-xs"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs">Período - início</Label>
                <Input
                  type="date"
                  className="h-8 text-xs"
                  value={filtrosDraft.dataInicio}
                  onChange={(e) => setFiltrosDraft((p) => ({ ...p, dataInicio: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">Período - fim</Label>
                <Input
                  type="date"
                  className="h-8 text-xs"
                  value={filtrosDraft.dataFim}
                  onChange={(e) => setFiltrosDraft((p) => ({ ...p, dataFim: e.target.value }))}
                />
              </div>

              <div>
                <Label className="text-xs">Status</Label>
                <Select
                  value={filtrosDraft.status || "todos"}
                  onValueChange={(v) => setFiltrosDraft((p) => ({ ...p, status: v === "todos" ? "" : v }))}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="aguardando pagamento">Aguardando pagamento</SelectItem>
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="pendente">Pendente</SelectItem>
                    <SelectItem value="cancelado">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs">Valor (mín)</Label>
                <Input
                  className="h-8 text-xs"
                  value={filtrosDraft.valorMin}
                  onChange={(e) => setFiltrosDraft((p) => ({ ...p, valorMin: e.target.value }))}
                  placeholder="0"
                />
              </div>
              <div>
                <Label className="text-xs">Valor (máx)</Label>
                <Input
                  className="h-8 text-xs"
                  value={filtrosDraft.valorMax}
                  onChange={(e) => setFiltrosDraft((p) => ({ ...p, valorMax: e.target.value }))}
                  placeholder="0"
                />
              </div>

              <div>
                <Label className="text-xs">Estado</Label>
                <Select
                  value={filtrosDraft.estado || "todos"}
                  onValueChange={(v) => {
                    const next = v === "todos" ? "" : v;
                    setFiltrosDraft((p) => ({ ...p, estado: next, cidade: "" }));
                  }}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {BRASIL_UFS.map((uf) => (
                      <SelectItem key={uf.sigla} value={uf.sigla}>
                        {uf.sigla} - {uf.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs">Cidade</Label>
                <Select
                  value={filtrosDraft.cidade || "todas"}
                  onValueChange={(v) => setFiltrosDraft((p) => ({ ...p, cidade: v === "todas" ? "" : v }))}
                  disabled={!filtrosDraft.estado}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder={filtrosDraft.estado ? "Todas" : "Selecione um estado"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas</SelectItem>
                    {cidadesDoEstado.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-3 flex justify-end gap-2">
              <Button className="h-8 px-3 text-xs" variant="secondary" onClick={handleLimparFiltros}>
                Limpar
              </Button>
              <Button className="h-8 px-3 text-xs bg-primary text-primary-foreground hover:bg-primary/90" onClick={handleAplicarFiltros}>
                Aplicar filtro
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="w-full overflow-x-auto">
          <Table className="min-w-[1100px] text-[11px] leading-tight">
            <TableHeader>
              <TableRow>
                {visibleContratoCols.includes("id") && <TableHead align="center">ID</TableHead>}
                {visibleContratoCols.includes("cliente") && <TableHead align="center">Cliente</TableHead>}
                {visibleContratoCols.includes("localizacao") && <TableHead align="center" className="hidden md:table-cell">Estado/Cidade</TableHead>}
                {visibleContratoCols.includes("valor") && <TableHead align="center">Valor</TableHead>}
                {visibleContratoCols.includes("status") && <TableHead align="center">Status</TableHead>}
                {visibleContratoCols.includes("renovacao") && <TableHead align="center">Renovação</TableHead>}
                {visibleContratoCols.includes("data_inicio") && <TableHead align="center" className="hidden md:table-cell">Data Início</TableHead>}
                {visibleContratoCols.includes("data_fim") && <TableHead align="center" className="hidden md:table-cell">Data Fim</TableHead>}
                {visibleContratoCols.includes("arquivo") && <TableHead align="center" className="hidden lg:table-cell">Arquivo</TableHead>}
                <TableHead align="center">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contratos?.map((contrato) => (
                <TableRow key={contrato.id} className="odd:bg-gray-50/60 hover:bg-gray-50">
                  {visibleContratoCols.includes("id") && (
                    <TableCell align="center" className="font-medium">
                      {contrato.id}
                    </TableCell>
                  )}
                  {visibleContratoCols.includes("cliente") && (
                    <TableCell align="left" className="font-medium max-w-[260px] truncate">
                      {contrato.cliente?.nome || `Cliente ID: ${contrato.cliente_id}`}
                    </TableCell>
                  )}
                  {visibleContratoCols.includes("localizacao") && (
                    <TableCell align="left" className="whitespace-nowrap text-gray-600 hidden md:table-cell">
                      {contrato.cliente?.estado || "—"} / {contrato.cliente?.cidade || "—"}
                    </TableCell>
                  )}
                  {visibleContratoCols.includes("valor") && (
                    <TableCell align="center" className="font-medium whitespace-nowrap">
                      {formatarValor(contrato.valor)}
                    </TableCell>
                  )}
                  {visibleContratoCols.includes("status") && (
                    <TableCell align="center">
                      <div className="flex justify-center">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs ${getStatusColor(contrato.status)}`}>
                          {contrato.status}
                        </span>
                      </div>
                    </TableCell>
                  )}
                  {visibleContratoCols.includes("renovacao") && (
                    <TableCell align="center" className="whitespace-nowrap">
                      {formatNumeroCicloVenda(contrato.numero_ciclo)}
                    </TableCell>
                  )}
                  {visibleContratoCols.includes("data_inicio") && (
                    <TableCell align="center" className="hidden md:table-cell whitespace-nowrap">
                      {formatarData(contrato.data_inicio)}
                    </TableCell>
                  )}
                  {visibleContratoCols.includes("data_fim") && (
                    <TableCell align="center" className="hidden md:table-cell whitespace-nowrap">
                      {formatarData(contrato.data_fim)}
                    </TableCell>
                  )}
                  {visibleContratoCols.includes("arquivo") && (
                    <TableCell align="left" className="hidden lg:table-cell">
                    {contrato.arquivo_pdf_url ? (
                      <div className="flex items-center gap-2 text-green-600">
                        <FileCheck className="h-4 w-4" />
                        <span className="text-sm">{contrato.arquivo_pdf_name || 'PDF anexado'}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-yellow-600 font-medium">Pendente</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => abrirModalAnexar(contrato)}
                          className="h-7 text-xs"
                          title="Anexar arquivo PDF"
                        >
                          <Upload className="h-3 w-3 mr-1" />
                          Anexar
                        </Button>
                      </div>
                    )}
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <div className="flex gap-1 justify-center">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => visualizarContrato(contrato)}
                        title="Visualizar"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => baixarPDF(contrato)}
                        title="Baixar PDF"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => editarContrato(contrato)}
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          
          {contratos && contratos.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Nenhum contrato encontrado
            </div>
          )}
          
          {Math.ceil(totalContratos / itemsPerPage) > 1 && (
            <div className="flex justify-center items-center gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Anterior
              </Button>
              <span className="text-gray-700">
                Página {currentPage} de {Math.ceil(totalContratos / itemsPerPage)}
              </span>
              <Button
                variant="outline"
                onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalContratos / itemsPerPage), prev + 1))}
                disabled={currentPage >= Math.ceil(totalContratos / itemsPerPage)}
              >
                Próxima
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Visualização */}
      <Dialog open={showVisualizarModal} onOpenChange={setShowVisualizarModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Detalhes do Contrato
            </DialogTitle>
          </DialogHeader>
          
          {contratoSelecionado && (
            <div className="space-y-6">
              {/* Informações Principais */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-500">Número do Contrato</Label>
                  <div className="text-lg font-semibold">{contratoSelecionado.numero_contrato || contratoSelecionado.id}</div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-500">Status</Label>
                  <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(contratoSelecionado.status)}`}>
                    {contratoSelecionado.status}
                  </span>
                </div>
              </div>

              {/* Cliente */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Cliente
                </Label>
                <div className="text-lg">{contratoSelecionado.cliente?.nome || `Cliente ID: ${contratoSelecionado.cliente_id}`}</div>
                {contratoSelecionado.cliente?.email && (
                  <div className="text-sm text-gray-600">{contratoSelecionado.cliente.email}</div>
                )}
                {contratoSelecionado.cliente?.telefone && (
                  <div className="text-sm text-gray-600">{contratoSelecionado.cliente.telefone}</div>
                )}
              </div>

              {/* Valor */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Valor
                </Label>
                <div className="text-2xl font-bold text-green-600">
                  {formatarValor(contratoSelecionado.valor)}
                </div>
              </div>

              {/* Datas */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-500 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Data de Início
                  </Label>
                  <div className="text-lg">{formatarData(contratoSelecionado.data_inicio)}</div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-500 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Data de Fim
                  </Label>
                  <div className="text-lg">{formatarData(contratoSelecionado.data_fim)}</div>
                </div>
              </div>

              {/* Arquivo */}
              {contratoSelecionado.arquivo_pdf_url && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-500 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Arquivo Anexado
                  </Label>
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <FileCheck className="h-5 w-5 text-green-600" />
                    <span className="text-sm">{contratoSelecionado.arquivo_pdf_name || 'PDF anexado'}</span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => baixarPDF(contratoSelecionado)}
                      className="ml-auto"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Baixar
                    </Button>
                  </div>
                </div>
              )}

              {/* Observações */}
              {contratoSelecionado.observacoes && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-500">Observações</Label>
                  <div className="p-3 bg-gray-50 rounded-lg text-sm">
                    {contratoSelecionado.observacoes}
                  </div>
                </div>
              )}

              {/* Informações do Sistema */}
              <div className="pt-4 border-t">
                <div className="text-xs text-gray-500 space-y-1">
                  <div>ID: {contratoSelecionado.id}</div>
                  <div>Criado em: {formatarData(contratoSelecionado.created_at)}</div>
                  <div>Atualizado em: {formatarData(contratoSelecionado.updated_at)}</div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Edição */}
      <Dialog open={showEditarModal} onOpenChange={setShowEditarModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-blue-600" />
              Editar Contrato
            </DialogTitle>
          </DialogHeader>
          
          {editandoContrato && (
            <div className="space-y-4">
              {/* Status */}
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select 
                  value={editandoContrato.status} 
                  onValueChange={(value) => setEditandoContrato({...editandoContrato, status: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aguardando pagamento">Aguardando pagamento</SelectItem>
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="pendente">Pendente</SelectItem>
                    <SelectItem value="cancelado">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Data de Fim */}
              <div className="space-y-2">
                <Label htmlFor="data_fim">Data de Fim</Label>
                <Input
                  id="data_fim"
                  type="date"
                  value={editandoContrato.data_fim || ''}
                  onChange={(e) => setEditandoContrato({...editandoContrato, data_fim: e.target.value})}
                />
              </div>

              {/* Observações */}
              <div className="space-y-2">
                <Label htmlFor="observacoes">Observações</Label>
                <Textarea
                  id="observacoes"
                  value={editandoContrato.observacoes || ''}
                  onChange={(e) => setEditandoContrato({...editandoContrato, observacoes: e.target.value})}
                  placeholder="Adicione observações sobre o contrato..."
                  rows={3}
                />
              </div>

              {/* Botões */}
              <div className="flex gap-2 pt-4">
                <Button 
                  onClick={salvarEdicao}
                  disabled={atualizarContrato.isPending}
                  className="flex-1"
                >
                  {atualizarContrato.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Salvar
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowEditarModal(false)}
                  disabled={atualizarContrato.isPending}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Anexar Arquivo */}
      <Dialog open={showAnexarModal} onOpenChange={(open) => {
        setShowAnexarModal(open);
        if (!open) {
          setArquivoParaAnexar(null);
          setContratoSelecionado(null);
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Anexar Contrato Assinado
            </DialogTitle>
          </DialogHeader>
          
          {contratoSelecionado && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="space-y-2">
                  <div>
                    <span className="text-sm font-medium text-gray-700">Contrato:</span>
                    <span className="ml-2 text-sm text-gray-900">
                      #{contratoSelecionado.numero_contrato || contratoSelecionado.id}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700">Cliente:</span>
                    <span className="ml-2 text-sm text-gray-900">
                      {contratoSelecionado.cliente?.nome || `Cliente ID: ${contratoSelecionado.cliente_id}`}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <Label>Selecionar Contrato Assinado (PDF) *</Label>
                <FileInput
                  label=""
                  accept=".pdf"
                  onFileSelect={(file) => {
                    setArquivoParaAnexar(file);
                  }}
                  selectedFile={arquivoParaAnexar}
                  placeholder="Selecionar arquivo PDF assinado..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Faça o upload do contrato após assinatura digital. Apenas arquivos PDF são aceitos.
                </p>
              </div>

              {arquivoParaAnexar && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-green-700">
                    <FileCheck className="h-4 w-4" />
                    <span className="text-sm font-medium">Arquivo selecionado: {arquivoParaAnexar.name}</span>
                  </div>
                  <p className="text-xs text-green-600 mt-1">
                    Tamanho: {(arquivoParaAnexar.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowAnexarModal(false);
                    setArquivoParaAnexar(null);
                    setContratoSelecionado(null);
                  }}
                  disabled={uploadingArquivo}
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={handleAnexarArquivo}
                  disabled={!arquivoParaAnexar || uploadingArquivo}
                  className="bg-primary hover:bg-primary/90"
                >
                  {uploadingArquivo ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Anexando...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Anexar Arquivo
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
